import { Button, Grid, IconButton, Typography, useMediaQuery } from '@mui/material';
import React, { useContext } from 'react'
import style from '../../../assets/styles/componentStyles/campaignContactStyles/campaignContactStyle.module.scss'
import globalStyles from "../../../assets/styles/global.module.scss";
import GlobalButton from '../../utility/globalButton/button';
import DeleteIcon from '@mui/icons-material/Delete';
import TuneIcon from '@mui/icons-material/Tune';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import { filtersInterface } from './contactList';
import { campaignContact } from '../../../models/axiosModel';
import { primaryColor } from '../../../utils/colors';
import { ContactContext, ContactContextPayload } from '../../../context/contactContext';
import { useGenericStore } from '../../../store/genericStore';
import CloseIcon from '@mui/icons-material/Close';
import { formatDateTime, thousandSeparator } from '../../../utils/constants';
import GlobalTooltip from '../../utility/tooltip/globalTooltip';
import GlobalSearch from '../../utility/GlobalSearch/GlobalSearch';
import { isArchivedContactList } from './contactConstant';
 
interface ContactPageHeaderProps {
    filters: filtersInterface,
    setFilters: React.Dispatch<React.SetStateAction<filtersInterface>>,
    selectContact: campaignContact[],
    setSelectContact: React.Dispatch<React.SetStateAction<campaignContact[]>>,
    deleteCampaignContact: (contacts: campaignContact[]) => void,
    setFlagFilterDialog: React.Dispatch<React.SetStateAction<boolean>>,
    downloadCSVFile: () => void,
    handleRefreshButtonClick: () => void, 
    filteredContacts: campaignContact[],
    handleClearFilter: () => void,
    searchQuery: string,
    setSearchQuery: React.Dispatch<React.SetStateAction<string>>,
}

const ContactPageHeader: React.FC<ContactPageHeaderProps> = (props) => {

    const { filters, setFilters, selectContact, setSelectContact, deleteCampaignContact, setFlagFilterDialog, downloadCSVFile, handleRefreshButtonClick, filteredContacts, handleClearFilter, searchQuery, setSearchQuery } = props;

    const breakPoints = {
        flagBelow1400Screen: useMediaQuery<boolean>('(min-width: 0px) and (max-width: 1400px)'),
        flagSmallScreen: useMediaQuery<boolean>('(min-width: 0px) and (max-width: 1000px)'),
        screen600to1300: useMediaQuery('(min-width: 600px) and (max-width: 1300px)'),
        mobileScreen: useMediaQuery('(min-width: 0px) and (max-width: 600px)'),
    }

    const { campaignContactData } = useContext(ContactContext) as ContactContextPayload;
    const globalStatusData = useGenericStore((state) => state.listOfChatbotStatus);

    const renderFilterChip = (text: string, handleCloseIconClick: () => void) => {
        return (
            <Grid item>
                <Button
                    className={style.filterButtons}
                    endIcon={
                        <CloseIcon
                            onClick={() => handleCloseIconClick()}
                        />
                    }
                >
                    {text}
                </Button>
            </Grid>
        );
    }

    const showClearFilterOption = () => {
        return (filters?.startDate || filters?.endDate || filters?.phoneNumber?.length > 0 || filters?.name?.length > 0 || filters?.tags?.length > 0 || filters?.includeDeletedContacts);
    }

    const getAppliedFilterCount = () => {
        let count = filters?.phoneNumber?.length + filters?.name?.length + filters?.tags?.length;
        if(filters?.startDate || filters?.endDate){
            count++;
        }
        if(filters?.includeDeletedContacts){
            count++;
        }
        return count;
    }

    const getContactCount = (flagDeleted: boolean) => {
        return campaignContactData?.[0]?.contact?.filter((contact: campaignContact) => contact?.flag_deleted === flagDeleted)?.length;
    }

    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
    }

    const isContactListArchived = isArchivedContactList(campaignContactData?.[0], globalStatusData);

    return (
        <>
            <Grid container gap={1}>
                <Grid item xs={12}>
                    <Grid container className={`${globalStyles.spaceAlignmentCenter} ${globalStyles.alignItemCenter}`} gap={2}>
                        <Grid item>
                            <Grid container gap={1}>
                                <Typography className={`${globalStyles.secondaryFontSize} ${globalStyles.secondaryFontWeight}`}>
                                    {getAppliedFilterCount() > 0 ?
                                        `Showing ${thousandSeparator(filteredContacts?.length)} of ${filters?.includeDeletedContacts ? thousandSeparator(campaignContactData?.[0]?.contact?.length ?? 0) : thousandSeparator(getContactCount(false))} contacts${filters?.includeDeletedContacts ? ' (Includes deleted)' : ''}`
                                        :
                                        filters?.includeDeletedContacts ? 
                                        `Contacts: ${thousandSeparator(filteredContacts?.length)} (Includes deleted)`
                                        :
                                        `Total Contacts: ${thousandSeparator(filteredContacts?.length)}`
                                    }
                                </Typography>
                                {isContactListArchived && 
                                    <span className={style.archivedTagForContactList}>
                                        Archived
                                    </span>
                                }
                            </Grid>
                        </Grid>
                        <Grid item>
                            <Grid container gap={1}>

                                {selectContact.length > 0 &&
                                    <Grid item>
                                        {breakPoints.mobileScreen ?
                                            <GlobalButton
                                                buttonText={`(${selectContact.length})`}
                                                className='deleteButton'
                                                startIcon={<DeleteIcon sx={{ color: "white" }} />}
                                                sx={{ padding: "6px 10px !important" }}
                                                onClick={() => { deleteCampaignContact(selectContact); setSelectContact([]) }}
                                            />
                                            :
                                            <GlobalButton
                                                buttonText={`Delete (${selectContact.length})`}
                                                className='deleteButton'
                                                sx={{ padding: "6px 20px !important" }}
                                                onClick={() => { deleteCampaignContact(selectContact); setSelectContact([]) }}
                                            />
                                        }
                                    </Grid>
                                }

                                <Grid item className={`${globalStyles.globalSearchbarSubItem}`}>
                                    <GlobalSearch
                                        fullWidth
                                        value={searchQuery}
                                        onChange={handleSearch}
                                        size="small"
                                        placeholder="Search"
                                        className={style.searchbar}
                                        sx={{
                                            '& .MuiOutlinedInput-input': {
                                                padding: '10px 8px !important',
                                            },
                                            '& .MuiOutlinedInput-input::placeholder': {
                                                fontSize: '15px !important',
                                            },
                                            boxShadow: '0 0 4px 0 rgba(0, 0, 0, 0.25) !important'
                                        }}
                                    />
                                </Grid>

                                <Grid item>
                                    <GlobalTooltip
                                        placement='bottom'
                                        arrow
                                        title="Filter"
                                    >
                                        <IconButton
                                            className={`${style.headerButtonStyle}`}
                                            onClick={() => { setFlagFilterDialog(true); }}
                                            sx={{ gap: "4px" }}
                                        >
                                            <TuneIcon className={`${globalStyles.primaryColor} ${globalStyles.displayFlexStyle}`} />
                                            {showClearFilterOption() && 
                                                <Grid item className={`${globalStyles.primaryFontSize} ${globalStyles.secondaryFontWeight} ${globalStyles.primaryColor} ${globalStyles.displayFlex}`} sx={{ lineHeight: 1, marginBottom: '2px' }}>
                                                    ({getAppliedFilterCount()})
                                                </Grid>
                                            }
                                        </IconButton>
                                    </GlobalTooltip>
                                </Grid>

                                <Grid item>
                                    <GlobalTooltip
                                        placement='bottom'
                                        arrow
                                        title="Download"
                                    >
                                        <IconButton
                                            className={style.headerButtonStyle}
                                            onClick={() => downloadCSVFile()}
                                        >
                                            <DownloadIcon sx={{ color: primaryColor }} />
                                        </IconButton>
                                    </GlobalTooltip>
                                </Grid>
                                <Grid item>
                                    <GlobalTooltip
                                        placement='bottom'
                                        arrow
                                        title="Refresh"
                                    >
                                        <IconButton
                                            className={style.headerButtonStyle}
                                            onClick={() => { handleRefreshButtonClick(); }}
                                        >
                                            <RefreshIcon sx={{ color: primaryColor }} />
                                        </IconButton>
                                    </GlobalTooltip>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>

                <Grid item xs={12}>
                    <Grid container justifyContent={breakPoints?.flagSmallScreen ? "" : "space-between"} alignItems={"center"} gap={1} flexWrap={breakPoints?.flagSmallScreen ? "wrap" : "nowrap"}>
                        <Grid item>
                            <Grid container gap={1} alignItems={"center"}>
                                {(filters?.startDate || filters?.endDate) && 
                                    <Grid item>
                                        {renderFilterChip(((filters?.startDate && !filters?.endDate) || (!filters?.startDate && filters?.endDate)) ? formatDateTime(filters?.startDate ?? filters?.endDate, "dmTextyShort") : `${formatDateTime(filters?.startDate, "dmTextyShort")} to ${formatDateTime(filters?.endDate, "dmTextyShort")}`, () => { setFilters((prev) => ({ ...prev, startDate: null, endDate: null }))})}
                                    </Grid>
                                }

                                {filters?.phoneNumber?.map((phoneNumber: string) => {
                                    return (
                                        <Grid item>
                                            {renderFilterChip(phoneNumber, () => {
                                                setFilters((prev) => ({
                                                    ...prev,
                                                    phoneNumber: prev?.phoneNumber?.filter((phone: string) => phone !== phoneNumber)
                                                }))
                                            })}
                                        </Grid>
                                    );
                                })}

                                {filters?.name?.map((name: string) => {
                                    return (
                                        <Grid item>
                                            {renderFilterChip(name, () => {
                                                setFilters((prev) => ({
                                                    ...prev,
                                                    name: prev?.name?.filter((filterName: string) => filterName !== name)
                                                }))
                                            })}
                                        </Grid>
                                    );
                                })}

                                {filters?.tags?.map((tag: string) => {
                                    return (
                                        <Grid item>
                                            {renderFilterChip(tag, () => {
                                                setFilters((prev) => ({
                                                    ...prev,
                                                    tags: prev?.tags?.filter((filterTag: string) => filterTag !== tag)
                                                }))
                                            })}
                                        </Grid>
                                    );
                                })}

                                {filters?.includeDeletedContacts && 
                                    <Grid item>
                                        {renderFilterChip("Include deleted contacts", () => {
                                            setFilters((prev) => ({
                                                ...prev,
                                                includeDeletedContacts: false
                                            }))
                                        })}
                                    </Grid>
                                }
                            </Grid>
                        </Grid>

                        {showClearFilterOption() && 
                            <Grid item>
                                <Typography className={style.clearFilterText} onClick={() => handleClearFilter()}>
                                    Clear All
                                </Typography>
                            </Grid>
                        }
                    </Grid>
                </Grid>
            </Grid>
        </>
    )
}

export default ContactPageHeader
